import { useEffect } from 'react'
import { useForm } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import ChangeContextFields from '@/frontend/features/Fde/ChangeContextFields'
import { FdeEditorDialog } from '@/frontend/features/Fde/FdeEditorSurface'
import { SelectControl, TextareaControl, TextControl } from '@/frontend/features/Fde/FormControls'
import OntologySchemaFieldEditor, { type ContractField } from '@/frontend/features/Fde/OntologySchemaFieldEditor'
import { fdeRoutes } from '@/frontend/features/Fde/routes'
import type { ActionType, Evidence, FdeCopy, OntologyClass, OntologyInterface } from '@/frontend/features/Fde/types'

interface Props { open: boolean; onOpenChange: (open: boolean) => void; record: ActionType | null; ontologyClasses: OntologyClass[]; ontologyInterfaces: OntologyInterface[]; evidences: Evidence[]; projectId: string; copy: FdeCopy }

export default function ActionTypeDialog({ open, onOpenChange, record, ontologyClasses, ontologyInterfaces, evidences, projectId, copy }: Props) {
  const form = useForm<{ name: string; description: string; target: string; input_schema: ContractField[]; preconditions: string; effects: string; change_note: string; evidence_id: string }>({ name: '', description: '', target: '', input_schema: [], preconditions: '', effects: '', change_note: '', evidence_id: '' })
  const targets = [
    ...ontologyInterfaces.map((item) => ({ value: `interface:${item.id}`, label: `${copy.workspace.interfaces} · ${item.name}` })),
    ...ontologyClasses.map((item) => ({ value: `class:${item.id}`, label: `${copy.workspace.object_types} · ${item.name}` })),
  ]

  useEffect(() => {
    if (!open) return
    form.setData({
      name: record?.name ?? '', description: record?.description ?? '',
      target: record ? `${record.target.kind}:${record.target.id}` : targets[0]?.value ?? '',
      input_schema: record?.input_schema ?? [], preconditions: record?.preconditions.join('\n') ?? '',
      effects: record?.effects.join('\n') ?? '', change_note: '', evidence_id: '',
    })
  }, [open, record?.id])

  function submit() {
    form.transform((data) => {
      const { target, ...attributes } = data
      const [kind, id] = target.split(':')
      return { action_type: {
        ...attributes, target_class_id: kind === 'class' ? id : '', target_interface_id: kind === 'interface' ? id : '',
        preconditions: data.preconditions.split('\n').map((value) => value.trim()).filter(Boolean),
        effects: data.effects.split('\n').map((value) => value.trim()).filter(Boolean),
      } }
    })
    const options = { preserveScroll: true, onSuccess: () => onOpenChange(false) }
    const query = `?project_id=${projectId}`
    if (record) form.patch(`${fdeRoutes.actionTypes}/${record.id}${query}`, options)
    else form.post(`${fdeRoutes.actionTypes}${query}`, options)
  }

  return (
    <FdeEditorDialog
      open={open}
      onOpenChange={onOpenChange}
      title={record ? copy.workspace.edit_action_type : copy.workspace.add_action_type}
      closeLabel={copy.common.close}
      footer={<><Button variant="outline" onClick={() => onOpenChange(false)}>{copy.common.cancel}</Button><Button disabled={form.processing || !form.data.name.trim() || !form.data.target} onClick={submit}>{copy.common.save}</Button></>}
    >
        <div className="grid gap-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <TextControl label={copy.forms.name} value={form.data.name} onChange={(event) => form.setData('name', event.target.value)} required />
            <SelectControl label={copy.forms.target} value={form.data.target} onChange={(value) => form.setData('target', value)} options={targets} placeholder={copy.forms.select_target} />
          </div>
          <TextareaControl label={copy.forms.description} value={form.data.description} onChange={(event) => form.setData('description', event.target.value)} rows={2} />
          <OntologySchemaFieldEditor fields={form.data.input_schema} onChange={(value) => form.setData('input_schema', value)} copy={copy} />
          <div className="grid gap-4 sm:grid-cols-2">
            <TextareaControl label={copy.forms.preconditions} value={form.data.preconditions} onChange={(event) => form.setData('preconditions', event.target.value)} rows={4} />
            <TextareaControl label={copy.forms.effects} value={form.data.effects} onChange={(event) => form.setData('effects', event.target.value)} rows={4} />
          </div>
          <ChangeContextFields changeNote={form.data.change_note} evidenceId={form.data.evidence_id} evidences={evidences} copy={copy} onChangeNote={(value) => form.setData('change_note', value)} onEvidenceId={(value) => form.setData('evidence_id', value)} />
        </div>
    </FdeEditorDialog>
  )
}
